import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {DeniedDevices, UserMediaStatus} from '@pexip/media';
import type {ExtendedSizeModifier, GroupProps} from '@pexip/components';
import {Button, Icon, IconTypes, Group} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import type {
    BlockedBrowserPermissionsInfoType,
    DeviceStatusInfo,
} from '../../types';
import type {TooltipPosition} from '../InputControl/PreflightInputControl.view';
import {PreflightInputControl} from '../InputControl/PreflightInputControl.view';
import {useInputsState} from '../../hooks/useInputsState';
import {BackgroundBlurInputControl} from '../InputControl/InputControl.view';
import {useInputSuspendedInfo} from '../../hooks/useInputSuspendedInfo';

export const PreflightInputControls: React.FC<
    GroupProps & {
        audioInputControl?: React.ReactNode;
        deniedDevice?: DeniedDevices;
        enhancerEnd?: React.ReactNode;
        inputStatusInfo: DeviceStatusInfo;
        isAudioInputMuted: boolean;
        isAudioInputSuspended: boolean;
        isAudioInputUnavailable: boolean;
        isBlurred?: boolean;
        isMobileDevice?: boolean;
        isRequestingAudio: boolean;
        isRequestingVideo: boolean;
        isVideoInputMuted: boolean;
        isVideoInputSuspended: boolean;
        isVideoInputUnavailable: boolean;
        learnHowToGrantAccessURL?: string;
        permissionInfoType: BlockedBrowserPermissionsInfoType;
        requestMediaPermissions: () => void;
        segmentationEnabled?: boolean;
        setShowHelpVideo: React.Dispatch<React.SetStateAction<boolean>>;
        silentTooltipInfo?: DeviceStatusInfo;
        streamStatus?: UserMediaStatus;
        toggleAudioInput?: () => void;
        toggleBlur?: () => void;
        toggleFacingMode?: () => void;
        toggleVideoInput?: () => void;
    }
> = ({
    audioInputControl,
    deniedDevice,
    enhancerEnd,
    inputStatusInfo,
    isAudioInputMuted,
    isAudioInputSuspended,
    isAudioInputUnavailable,
    isBlurred,
    isMobileDevice = false,
    isRequestingAudio,
    isRequestingVideo,
    isVideoInputMuted,
    isVideoInputSuspended,
    isVideoInputUnavailable,
    learnHowToGrantAccessURL,
    permissionInfoType,
    requestMediaPermissions,
    segmentationEnabled,
    setShowHelpVideo,
    silentTooltipInfo,
    streamStatus,
    toggleAudioInput,
    toggleBlur,
    toggleFacingMode,
    toggleVideoInput,
    ...props
}) => {
    const {t} = useTranslation();

    const {
        audioProps,
        videoProps,
        warning,
        isAudioIconMuted,
        isVideoIconMuted,
    } = useInputsState({
        isAudioInputMuted,
        isVideoInputMuted,
        inputStatusInfo,
        iconSize: 'medium',
    });

    const [isAudioTooltipVisible, setIsAudioTooltipVisible] = useState(
        warning === DeniedDevices.Microphone,
    );
    const [isVideoTooltipVisible, setIsVideoTooltipVisible] = useState(
        warning === DeniedDevices.Both || warning === DeniedDevices.Camera,
    );

    const suspendedInfo = useInputSuspendedInfo(
        isAudioInputSuspended,
        isVideoInputSuspended,
    );

    useEffect(() => {
        if (isAudioInputSuspended) {
            return;
        }
        if (silentTooltipInfo && !isVideoTooltipVisible) {
            setIsAudioTooltipVisible(true);
        } else {
            setIsAudioTooltipVisible(false);
        }
    }, [isVideoTooltipVisible, silentTooltipInfo, isAudioInputSuspended]);

    useEffect(() => {
        setIsVideoTooltipVisible(
            warning === DeniedDevices.Camera || warning === DeniedDevices.Both,
        );
    }, [warning]);

    useEffect(() => {
        setIsAudioTooltipVisible(warning === DeniedDevices.Microphone);
    }, [warning]);

    const [disableBgButton, setDisableBgButton] = useState(true);

    useEffect(() => {
        if (
            streamStatus &&
            ![
                UserMediaStatus.InitialPermissionsNotGranted,
                UserMediaStatus.InitialPermissionsGrantedVideoInputDenied,
            ].includes(streamStatus)
        ) {
            setDisableBgButton(
                isVideoInputMuted ||
                    warning === DeniedDevices.Camera ||
                    warning === DeniedDevices.Both,
            );
        }
    }, [streamStatus, warning, isVideoInputMuted]);

    const coreInputProps: {
        size: ExtendedSizeModifier;
        tooltipPosition: TooltipPosition;
    } = {
        size: 'large',
        tooltipPosition: isMobileDevice ? 'topCenter' : 'topEnd',
    };
    let audioStatusInfo = silentTooltipInfo;
    if (
        (inputStatusInfo.error || inputStatusInfo.warning) ===
        DeniedDevices.Microphone
    ) {
        audioStatusInfo = inputStatusInfo;
    }
    audioStatusInfo = suspendedInfo.audio ?? audioStatusInfo;
    let videoStatusInfo =
        (inputStatusInfo?.error || inputStatusInfo?.warning) ===
            DeniedDevices.Camera ||
        (inputStatusInfo?.error || inputStatusInfo?.warning) ===
            DeniedDevices.Both
            ? inputStatusInfo
            : undefined;
    videoStatusInfo = suspendedInfo.video;

    const isBothError =
        audioStatusInfo?.error === DeniedDevices.Both ||
        videoStatusInfo?.error === DeniedDevices.Both;

    let handleAudioBtnClick = toggleAudioInput;
    let handleVideoBtnClick = toggleVideoInput;
    if (audioStatusInfo?.error || audioStatusInfo?.warning) {
        handleAudioBtnClick = () =>
            setIsAudioTooltipVisible(visible => !visible);
    } else if (isBothError) {
        handleAudioBtnClick = () =>
            setIsVideoTooltipVisible(visible => !visible);
    }
    if (videoStatusInfo?.error || videoStatusInfo?.warning) {
        handleVideoBtnClick = () =>
            setIsVideoTooltipVisible(visible => !visible);
    }

    return (
        <Group
            spacing={isMobileDevice ? 'small' : 'medium'}
            justifyContent="center"
            {...props}
        >
            {!isMobileDevice && !!segmentationEnabled && (
                <BackgroundBlurInputControl
                    variant="transparentAlternative"
                    size="large"
                    isBlurred={isBlurred}
                    isDisabled={isVideoInputUnavailable || disableBgButton}
                    onClick={toggleBlur}
                    iconSize="medium"
                    data-testid={
                        isBlurred
                            ? TestId.ButtonBackgroundEffectsOn
                            : TestId.ButtonBackgroundEffectsOff
                    }
                />
            )}
            {toggleFacingMode && (
                <Button
                    aria-label={t('media.flip-camera', 'Flip camera')}
                    data-testid={TestId.ButtonFlipCamera}
                    modifier="square"
                    onClick={toggleFacingMode}
                    size="large"
                    variant="transparentAlternative"
                    disabled={isVideoInputMuted}
                    isLoading={isRequestingVideo}
                >
                    <Icon source={IconTypes.IconFlipCamera} size="medium" />
                </Button>
            )}
            {enhancerEnd && enhancerEnd}
            {
                <PreflightInputControl
                    {...audioProps}
                    {...coreInputProps}
                    isDisabled={isAudioInputUnavailable}
                    isLoading={isRequestingAudio}
                    isSuspended={isAudioInputSuspended}
                    isTooltipVisible={
                        !isAudioInputUnavailable && isAudioTooltipVisible
                    }
                    setIsTooltipVisible={setIsAudioTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipAudioProblem}
                    data-testid={
                        isAudioIconMuted
                            ? TestId.ButtonAudioMeterMuted
                            : TestId.ButtonAudioMeter
                    }
                    onClick={handleAudioBtnClick}
                    requestMediaPermissions={requestMediaPermissions}
                    testIdCloseButton={
                        TestId.NotificationToolTipAudioCloseButton
                    }
                    variant="transparentAlternative"
                    control={audioInputControl}
                    inputStatusInfo={audioStatusInfo}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
            {
                <PreflightInputControl
                    {...videoProps}
                    {...coreInputProps}
                    isDisabled={isVideoInputUnavailable}
                    isLoading={isRequestingVideo}
                    isSuspended={isVideoInputSuspended}
                    isTooltipVisible={
                        !isVideoInputUnavailable && isVideoTooltipVisible
                    }
                    setIsTooltipVisible={setIsVideoTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipVideoProblem}
                    data-testid={
                        isVideoIconMuted
                            ? TestId.ButtonVideoinputMuted
                            : TestId.ButtonVideoInput
                    }
                    onClick={handleVideoBtnClick}
                    requestMediaPermissions={requestMediaPermissions}
                    testIdCloseButton={
                        TestId.NotificationToolTipVideoCloseButton
                    }
                    variant="transparentAlternative"
                    inputStatusInfo={videoStatusInfo}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
        </Group>
    );
};

export type PreflightInputControlsProps = React.ComponentProps<
    typeof PreflightInputControls
>;
